import { Container, Rectangle, type FederatedPointerEvent, type Sprite, type Texture } from "pixi.js";

export type ButtonVisualState = "normal" | "hover" | "pressed" | "disabled";
export type ButtonStateTints = Record<ButtonVisualState, number>;

/** The `Button` / `ButtonMultiTargetStyle` defaults: 255 → 220 → 180 → 128 grey. */
export const DEFAULT_BUTTON_TINTS: ButtonStateTints = { normal: 0xffffff, hover: 0xdcdcdc, pressed: 0xb4b4b4, disabled: 0x808080 };

/**
 * One styled node — a `ButtonMultiTargetStyle` entry. `tints` recolour the node per state (a
 * missing state keeps the default); `textures` swap a sprite's texture per state (a missing
 * state keeps whatever the previous state left, as `applySpriteFrame` did).
 */
export interface ButtonStyleTarget {
  node: Container;
  tints?: Partial<ButtonStateTints>;
  textures?: Partial<Record<ButtonVisualState, Texture>>;
}

export interface ButtonOptions {
  /** The visual the button tints and scales (a sprite or a container of them). */
  view: Container;
  /** Tints per state on `view`. */
  normalTint?: number;
  hoverTint?: number;
  pressedTint?: number;
  disabledTint?: number;
  /** `false` = no colour transition on `view`; the targets carry the styling. */
  tintView?: boolean;
  /** Extra styled nodes. */
  targets?: ButtonStyleTarget[];
  /** Pressed-state scale; 1 = none. */
  pressedScale?: number;
  /** Tap surface. Default: the children's bounds. */
  hitBox?: { width: number; height: number };
  onTap?: () => void;
  label?: string;
}

/**
 * The Pixi button for the game panels (the DOM chrome has its own): a tinted / texture-swapped view
 * per state, extra styled targets, a pressed zoom, `interactable`. `pointertap` fires `onTap` only
 * while interactable; hover comes from the mouse only; a press ends on release, release-outside or
 * pointer-out. Press cues are the caller's (the panels play theirs through the audio service).
 */
export class Button extends Container {
  public readonly view: Container;
  private viewTints: ButtonStateTints;
  private readonly tintView: boolean;
  private readonly targets: ButtonStyleTarget[];
  private readonly pressedScale: number;
  /** The view's authored scale (a sized `Sprite` view lives in its scale) — the pressed zoom multiplies it. */
  private readonly viewBaseScale: { x: number; y: number };
  private interactableValue = true;
  private pressed = false;
  private hovered = false;
  private currentState: ButtonVisualState | null = null;
  private onTap: (() => void) | null;

  public constructor(options: ButtonOptions) {
    super({ label: options.label ?? "Button" });
    this.view = options.view;
    this.addChild(this.view);
    this.viewTints = {
      normal: options.normalTint ?? DEFAULT_BUTTON_TINTS.normal,
      hover: options.hoverTint ?? DEFAULT_BUTTON_TINTS.hover,
      pressed: options.pressedTint ?? DEFAULT_BUTTON_TINTS.pressed,
      disabled: options.disabledTint ?? DEFAULT_BUTTON_TINTS.disabled,
    };
    this.tintView = options.tintView ?? true;
    this.targets = options.targets ?? [];
    this.pressedScale = options.pressedScale ?? 1;
    this.viewBaseScale = { x: this.view.scale.x, y: this.view.scale.y };
    this.onTap = options.onTap ?? null;
    if (options.hitBox) this.setHitBox(options.hitBox.width, options.hitBox.height);
    this.eventMode = "static";
    this.cursor = "pointer";
    this.on("pointerdown", () => this.handlePressStart());
    this.on("pointerup", () => this.setPressed(false));
    this.on("pointerupoutside", () => this.setPressed(false));
    this.on("pointercancel", () => this.clearPointer());
    this.on("pointerover", (event: FederatedPointerEvent) => {
      if (event.pointerType === "mouse") this.setHovered(true);
    });
    this.on("pointerout", () => this.clearPointer());
    this.on("pointertap", () => {
      if (!this.interactableValue) return;
      this.onTap?.();
    });
    this.applyState();
  }

  public setOnTap(handler: (() => void) | null): void {
    this.onTap = handler;
  }

  /** The `UITransform` box, anchor 0.5. */
  public setHitBox(width: number, height: number): void {
    this.hitArea = new Rectangle(-width / 2, -height / 2, width, height);
  }

  public get interactable(): boolean {
    return this.interactableValue;
  }

  public set interactable(value: boolean) {
    if (this.interactableValue === value) return;
    this.interactableValue = value;
    this.cursor = value ? "pointer" : "default";
    if (!value) this.pressed = false;
    this.applyState();
  }

  /** Show / hide the whole button (presenters call this instead of touching `visible`). */
  public setShown(shown: boolean): void {
    this.visible = shown;
  }

  /** The `interactable` setter as a method: the one write path for the flag, the cursor and the styled visuals. */
  public setInteractable(value: boolean): void {
    this.interactable = value;
  }

  /** Replace the view's colour tiers and re-apply the current state. */
  public setViewTints(tints: Partial<ButtonStateTints>): void {
    this.viewTints = { ...this.viewTints, ...tints };
    this.currentState = null;
    this.applyState();
  }

  private handlePressStart(): void {
    if (!this.interactableValue) return;
    this.setPressed(true);
  }

  private setPressed(value: boolean): void {
    this.pressed = value && this.interactableValue;
    this.applyState();
  }

  private setHovered(value: boolean): void {
    this.hovered = value;
    this.applyState();
  }

  private clearPointer(): void {
    this.hovered = false;
    this.pressed = false;
    this.applyState();
  }

  private resolveState(): ButtonVisualState {
    if (!this.interactableValue) return "disabled";
    if (this.pressed) return "pressed";
    if (this.hovered) return "hover";
    return "normal";
  }

  private applyState(): void {
    const state = this.resolveState();
    if (state === this.currentState) return;
    this.currentState = state;
    if (this.tintView) this.view.tint = this.viewTints[state];
    const s = state === "pressed" ? this.pressedScale : 1;
    this.view.scale.set(this.viewBaseScale.x * s, this.viewBaseScale.y * s);
    for (const target of this.targets) {
      if (target.node.destroyed) continue;
      if (target.tints) target.node.tint = target.tints[state] ?? DEFAULT_BUTTON_TINTS[state];
      const texture = target.textures?.[state];
      if (texture) (target.node as Sprite).texture = texture;
    }
  }
}
